import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { typography } from '../../../../theme/tokens/typography';
import { radiusPx } from '../../../../theme/tokens/radius';

const NOTCHED_OUTLINE = `& .${outlinedInputClasses.notchedOutline}`;
const NOTCHED_HOVER = `&:hover .${outlinedInputClasses.notchedOutline}`;
const NOTCHED_FOCUSED = `&.Mui-focused .${outlinedInputClasses.notchedOutline}`;
const NOTCHED_ERROR_FOCUSED = `&.Mui-error.Mui-focused .${outlinedInputClasses.notchedOutline}`;

/**
 * Theme-level overrides for MUI outlined form controls.
 * Used by buildFeatureTheme for consistent form fields in feature modules.
 */
export function getFormControlThemeOverrides({
  resolvedSemantic,
  controlHeights,
  inputSize = 'sm',
}) {
  const inputs = resolvedSemantic.inputs ?? {};
  const borders = resolvedSemantic.borders ?? {};
  const interactive = resolvedSemantic.interactive ?? {};
  const surfaces = resolvedSemantic.surfaces ?? {};
  const text = resolvedSemantic.text ?? {};
  const states = resolvedSemantic.states ?? {};
  const minHeight = inputs.controlHeight ?? controlHeights?.[inputSize] ?? controlHeights?.md;
  const fieldBackground = inputs.background || surfaces.surface;
  const defaultBorder = inputs.border || borders.border;

  return {
    root: {
      minHeight,
      borderRadius: inputs.radius || radiusPx.md,
      backgroundColor: fieldBackground,
      transition: 'border-color 160ms ease, background-color 160ms ease',
      [NOTCHED_OUTLINE]: {
        top: 0,
        borderColor: defaultBorder,
        borderWidth: '1px !important',
        transition: 'border-color 160ms ease',
      },
      '&:hover': {
        backgroundColor: inputs.backgroundHover || fieldBackground,
      },
      [NOTCHED_HOVER]: {
        borderColor: borders.borderStrong ?? defaultBorder,
        borderWidth: '1px !important',
      },
      '&.Mui-focused': {
        backgroundColor: inputs.backgroundFocus || fieldBackground,
        boxShadow: 'none',
        outline: 'none',
      },
      [NOTCHED_FOCUSED]: {
        borderColor: interactive.primary,
        borderWidth: '1px !important',
      },
      '&.Mui-disabled': {
        backgroundColor: inputs.backgroundDisabled || fieldBackground,
        cursor: inputs.cursorDisabled || 'not-allowed',
        opacity: 0.72,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: states.danger,
        borderWidth: '1px !important',
      },
      '&.Mui-error.Mui-focused': {
        boxShadow: 'none',
      },
      [NOTCHED_ERROR_FOCUSED]: {
        borderColor: states.danger,
        borderWidth: '1px !important',
      },
    },
    input: {
      fontSize: inputs.fontSize || typography.fontSizeMd,
      color: text.primary,
      paddingTop: inputs.paddingY ?? 10,
      paddingBottom: inputs.paddingY ?? 10,
      paddingLeft: inputs.paddingX ?? 12,
      paddingRight: inputs.paddingX ?? 12,
      '&::placeholder': {
        color: text.muted,
        opacity: 1,
      },
      '&.Mui-disabled': {
        cursor: inputs.cursorDisabled || 'not-allowed',
        WebkitTextFillColor: text.muted,
      },
    },
    notchedOutline: {
      top: 0,
      borderWidth: '1px !important',
      legend: {
        display: 'none',
      },
    },
  };
}

export function getFormHelperTextThemeOverrides(resolvedSemantic) {
  return {
    root: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 6,
      fontSize: typography.fontSizeXs,
      lineHeight: typography.lineHeightBody,
      color: resolvedSemantic.text?.muted,
      '&.Mui-error': {
        color: resolvedSemantic.states?.danger,
      },
    },
  };
}

export function getFormLabelThemeOverrides(resolvedSemantic) {
  return {
    root: {
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightMedium,
      color: resolvedSemantic.text?.primary,
      '&.Mui-focused': {
        color: resolvedSemantic.text?.primary,
      },
      '&.Mui-error': {
        color: resolvedSemantic.states?.danger,
      },
      '&.MuiInputLabel-root.MuiInputLabel-shrink': {
        top: '-2.3px',
      },
    },
  };
}

/**
 * Optional per-component sx merge for one-off field overrides (higher specificity than theme).
 */
export function getFormControlSx({ semantic, radiusPx: radiusTokens, typography: typo }, extraSx = {}) {
  const inputs = semantic.inputs ?? {};
  const borders = semantic.borders ?? {};
  const defaultBorder = inputs.border || borders.border;

  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: inputs.radius || radiusTokens.md,
      backgroundColor: inputs.background || semantic.surfaces?.surface,
      fontSize: typo.fontSizeMd,
      '& fieldset': {
        borderColor: defaultBorder,
        borderWidth: '1px !important',
      },
      '&:hover fieldset': {
        borderColor: borders.borderStrong ?? defaultBorder,
        borderWidth: '1px !important',
      },
      '&.Mui-focused': {
        boxShadow: 'none',
      },
      '&.Mui-focused fieldset': {
        borderColor: semantic.interactive?.primary,
        borderWidth: '1px !important',
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginTop: 0.75,
      fontSize: typo.fontSizeXs,
    },
    ...extraSx,
  };
}
